/**
 * The first-login wall.
 *
 * An account an administrator created is on a password that administrator read
 * out loud, wrote in a chat message, or left in a ticket. Until the person
 * replaces it, the account has a second owner. So this is a WALL and not a
 * prompt: it renders instead of the app, there is no "later", and the flag that
 * dismisses it is cleared by exactly one thing — `POST /auth/password`
 * succeeding.
 *
 * Signing out is the only other way past, which is deliberate. Somebody who
 * cannot set a password right now should be able to leave; they simply cannot
 * take the workspace with them.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';

import { Button, Callout, Field, Input } from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import { ROUTES } from '@/lib/constants';

const MIN_LENGTH = 12;

export function ChangePasswordGate({ email }: { email: string }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      request<{ ok: true }>('/auth/password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      }),
    onSuccess: () => setDone(true),
  });

  /* Mismatch is surfaced only once the reader has committed to a confirmation,
     not on the first keystroke of an obviously-incomplete value. */
  const mismatch = confirm.length >= next.length && next.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const canSubmit =
    current.length > 0 && next.length >= MIN_LENGTH && next === confirm && !change.isPending;

  const error = change.isError
    ? ApiError.is(change.error)
      ? change.error.message
      : 'The password was not changed.'
    : null;

  if (done) {
    return (
      <div className="flex h-full w-full items-center justify-center px-8 py-12">
        <div className="flex w-full max-w-[420px] flex-col gap-4">
          <Callout variant="success" icon={KeyRound} title="Password changed">
            Every session was signed out, including this one. Sign in again with your new password —
            that is also how you know it is the one you think it is.
          </Callout>
          <Button variant="primary" onClick={() => window.location.assign(ROUTES.login)}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center px-8 py-12">
      <form
        className="flex w-full max-w-[420px] flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) change.mutate();
        }}
      >
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-primary">Choose your password</h1>
          <p className="text-sm text-secondary">
            {email} is signed in with a password an administrator generated. Replace it before you
            go any further.
          </p>
        </div>

        {error !== null ? (
          <Callout variant="danger" compact>
            {error}
          </Callout>
        ) : null}

        <Field label="Current password" hint="The one you were given." htmlFor="af-pw-current">
          <Input
            id="af-pw-current"
            type="password"
            value={current}
            autoComplete="current-password"
            autoFocus
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>

        <Field
          label="New password"
          hint={`At least ${MIN_LENGTH} characters.`}
          error={tooShort ? `Use at least ${MIN_LENGTH} characters` : undefined}
          htmlFor="af-pw-new"
        >
          <Input
            id="af-pw-new"
            type="password"
            value={next}
            autoComplete="new-password"
            onChange={(event) => setNext(event.target.value)}
          />
        </Field>

        <Field
          label="Confirm new password"
          error={mismatch ? 'These do not match' : undefined}
          htmlFor="af-pw-confirm"
        >
          <Input
            id="af-pw-confirm"
            type="password"
            value={confirm}
            autoComplete="new-password"
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>

        <Button type="submit" variant="primary" disabled={!canSubmit} loading={change.isPending}>
          Set password
        </Button>
      </form>
    </div>
  );
}

export default ChangePasswordGate;
